import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { useI18n } from '../i18n';
import { getFirebase, isDemoMode } from '../lib/firebase';
import { findTerm } from '../features/glossary/terms';

export function ScreenHeader({
  title,
  subtitle,
  back,
  right,
}: {
  title: string;
  subtitle?: string;
  back?: boolean;
  right?: ReactNode;
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <div className="spread" style={{ marginBottom: 14 }}>
      <div className="row">
        {back && (
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('common.back')}
            // A long title must never squeeze the arrow out of reach.
            style={{ flexShrink: 0 }}
            onClick={() => navigate(-1)}
          >
            <span aria-hidden>←</span>
          </button>
        )}
        <div>
          <div className="h1">{title}</div>
          {subtitle && <div className="muted small">{subtitle}</div>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function GradeBadge({ grade }: { grade: string }) {
  return <span className={`badge badge--${grade}`}>{grade}</span>;
}

export function Section({
  title,
  children,
  term,
}: {
  title: string;
  children: ReactNode;
  /** Glossary id or alias — renders an InfoDot beside the heading. */
  term?: string;
}) {
  return (
    <div>
      <div
        className="section-title"
        style={term ? { display: 'flex', alignItems: 'center', gap: 2 } : undefined}
      >
        {title}
        {term && <InfoDot term={term} />}
      </div>
      {children}
    </div>
  );
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/* --- Profile pictures ------------------------------------------------------

   FETCHED BY PATH, NEVER BY DOWNLOAD URL. A Firebase Storage download URL
   carries a token that bypasses storage.rules for anyone holding the link, so
   the documents only ever carry an object path and every read is evaluated
   against the rules. getBlob() is what does that; getDownloadURL() is what
   would undo it. (Consequence: the bucket needs a CORS entry for the app
   origins, because a rule-checked fetch is cross-origin from the WebView.)

   Avatars are readable by any signed-in user — deliberately, because gating
   them behind isPro() would add a Firestore read to every discovery card. They
   are kept tiny instead: 256px WebP under 30KB. */

/**
 * Resolved object URLs, keyed by path.
 *
 * Discovery re-renders its list constantly (a pass, a save, a quota refresh)
 * and every render would otherwise re-download every face. Object paths are
 * UUIDs under a write-once prefix, so a path always means the same bytes and
 * this can be cached for the lifetime of the tab.
 *
 * The URLs are deliberately NOT revoked: revoking one that an <img> still
 * points at blanks that image, and there is no reliable moment when the last
 * card holding it has unmounted. The map size is the bound instead — at 30KB
 * an avatar, the cap is a few megabytes at worst.
 */
const AVATAR_URL_CACHE = new Map<string, Promise<string | null>>();
const AVATAR_URL_CACHE_MAX = 200;

/** Already a renderable source? Demo mode stores the picture as a data URL. */
function directSrc(path: string): string | null {
  return /^(?:data:|blob:|https?:)/.test(path) ? path : null;
}

type FirebaseStorage = import('firebase/storage').FirebaseStorage;

let storageHandle: Promise<FirebaseStorage> | null = null;

/**
 * Storage is loaded on demand rather than imported at the top of the module.
 * `common.tsx` is imported by nearly every screen, and a demo-mode session
 * never touches a bucket at all — there is no reason for the SDK to be in the
 * first chunk the app parses.
 *
 * Exported because AvatarPicker uploads through the same handle. It lives here
 * rather than in lib/firebase.ts only because that file owns the other three
 * SDKs and is not in scope for this change; one owner is the point, so that
 * this emulator wiring exists exactly once.
 */
export function appStorage(): Promise<FirebaseStorage> {
  if (!storageHandle) {
    storageHandle = import('firebase/storage').then(({ getStorage, connectStorageEmulator }) => {
      const s = getStorage(getFirebase().app);
      // Mirrors the wiring in lib/firebase.ts for the other three SDKs; it
      // belongs there next to them once that file is being edited anyway.
      if (import.meta.env.VITE_USE_EMULATORS === 'true') {
        connectStorageEmulator(s, '127.0.0.1', 9199);
      }
      return s;
    });
  }
  return storageHandle;
}

async function fetchObjectUrl(path: string): Promise<string | null> {
  if (isDemoMode()) return null;
  try {
    const [{ getBlob, ref }, s] = await Promise.all([import('firebase/storage'), appStorage()]);
    return URL.createObjectURL(await getBlob(ref(s, path)));
  } catch {
    // Offline, signed out, deleted object, or a rule that said no. Every one
    // of them means "draw the initials", never "break the screen".
    return null;
  }
}

/** Object URL for a Storage path, cached. Resolves null when unavailable. */
export function mediaObjectUrl(path: string): Promise<string | null> {
  const direct = directSrc(path);
  if (direct) return Promise.resolve(direct);

  const cached = AVATAR_URL_CACHE.get(path);
  if (cached) return cached;

  const pending = fetchObjectUrl(path).then((url) => {
    // A failure is not cached: an avatar that missed because the device was
    // offline for a second must not stay missing for the whole session.
    if (!url) AVATAR_URL_CACHE.delete(path);
    return url;
  });
  AVATAR_URL_CACHE.set(path, pending);
  if (AVATAR_URL_CACHE.size > AVATAR_URL_CACHE_MAX) {
    const oldest = AVATAR_URL_CACHE.keys().next().value;
    if (oldest !== undefined && oldest !== path) AVATAR_URL_CACHE.delete(oldest);
  }
  return pending;
}

function useObjectUrl(path?: string): string | null {
  const [url, setUrl] = useState<string | null>(() => (path ? directSrc(path) : null));
  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    const direct = directSrc(path);
    if (direct) {
      setUrl(direct);
      return;
    }
    let live = true;
    setUrl(null);
    mediaObjectUrl(path).then((resolved) => {
      if (live) setUrl(resolved);
    });
    return () => {
      live = false;
    };
  }, [path]);
  return url;
}

/**
 * A profile picture, with the initials monogram underneath it.
 *
 * The monogram is not a spinner — it is the real fallback, drawn immediately
 * and left in place for an account with no picture, for one whose image is
 * still arriving, and for one whose fetch failed. There is never a blank hole
 * and never a layout shift; the photo simply appears over the top.
 */
export function Avatar({
  name,
  path,
  size = 44,
  gold = false,
}: {
  name: string;
  /** Storage object path from `UserDoc.avatar.path` / `DiscoveryCard.avatarPath`. */
  path?: string;
  size?: number;
  gold?: boolean;
}) {
  const src = useObjectUrl(path);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  return (
    <div
      className={`avatar${gold ? ' avatar--gold' : ''}`}
      style={{ width: size, height: size, position: 'relative', overflow: 'hidden' }}
    >
      {/* Hidden from assistive tech: every call site prints the name beside it,
          and "AK" read aloud before "Aditi Kulkarni" is noise. */}
      <span aria-hidden>{initialsOf(name)}</span>
      {src && !failed && (
        <img
          src={src}
          alt=""
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
}

/* --- Glossary affordances --------------------------------------------------

   InfoDot is the "explain this word" hook that can sit next to any piece of
   jargon in the app. It reads as a superscript circled i, but the actual
   button is a full 44×44 target pulled back with negative margins so it does
   not push the text it annotates around. */

const DOT_HIT: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  // Collapses the laid-out footprint back to ~22px while the tap target stays 44.
  margin: '-11px -12px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  verticalAlign: 'middle',
  flexShrink: 0,
};

const DOT_GLYPH: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '1px solid var(--accent-line)',
  background: 'var(--accent-soft)',
  color: 'var(--accent-ink)',
  fontSize: '0.75rem',
  fontWeight: 700,
  fontStyle: 'italic',
  lineHeight: 1,
  // .section-title uppercases and letter-spaces its contents; the glyph opts out.
  textTransform: 'none',
  letterSpacing: 'normal',
  // The superscript lift, done without vertical-align on a flex box.
  position: 'relative',
  top: -6,
};

export function InfoDot({ term }: { term: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const entry = findTerm(term);
  // An unknown term renders nothing rather than a dot that explains nothing.
  if (!entry) return null;
  return (
    <>
      <button
        type="button"
        style={DOT_HIT}
        aria-label={t('glossary.moreOnTerm', { term: entry.title })}
        onClick={(e) => {
          // Section headings and cards are often clickable themselves.
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <span aria-hidden style={DOT_GLYPH}>
          i
        </span>
      </button>
      {open && <TermSheet term={entry.id} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Bottom sheet explaining one glossary term. */
export function TermSheet({ term, onClose }: { term: string; onClose: () => void }) {
  const { t } = useI18n();
  const sheetRef = useRef<HTMLDivElement>(null);
  const entry = findTerm(term);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!entry) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 95,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="term-sheet-title"
        tabIndex={-1}
        className="card card--strong fade-in"
        style={{
          width: '100%',
          maxWidth: 560,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          // Clears the gesture pill on edge-to-edge Android.
          paddingBottom: 'calc(16px + var(--safe-bottom))',
          maxHeight: '80vh',
          overflowY: 'auto',
          outline: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div className="h2" id="term-sheet-title">
            {entry.title}
          </div>
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <span aria-hidden>✕</span>
          </button>
        </div>

        <div className="small" style={{ marginTop: 6, color: 'var(--accent-ink)' }}>
          {entry.short}
        </div>
        {entry.western && (
          <div className="tiny faint" style={{ marginTop: 6 }}>
            {t('glossary.westernEquiv', { name: entry.western })}
          </div>
        )}
        <div className="small muted" style={{ marginTop: 10, lineHeight: 1.6 }}>
          {entry.long}
        </div>

        <Link
          to={`/glossary#${entry.id}`}
          className="chip"
          style={{ marginTop: 14, color: 'var(--accent-ink)' }}
          onClick={onClose}
        >
          {t('glossary.seeAll')} →
        </Link>
      </div>
    </div>
  );
}
